import { AfterViewInit, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import { Observable } from 'rxjs';
import { Store } from '@ngrx/store';
import { map } from 'rxjs/operators';
import { GapMenu } from 'guest-app-ui';
import { AppState } from 'src/app/app.state';
import { selectAppConfig } from 'src/app/core/application-config/application-config.selectors';
import { selectLanguage } from 'src/app/core/client-configuration/client-configuration.selectors';
import { setLanguage } from 'src/app/core/client-configuration/client-configuration.actions';
import { validString } from 'src/app/shared/utilities/types.utils';

@Component({
	selector: 'ahp-swap-language',
	templateUrl: './swap-language.component.html',
	styleUrls: ['./swap-language.component.scss'],
})
export class SwapLanguageComponent implements AfterViewInit, OnDestroy {
	@Input() isSideNav: boolean;

	@ViewChild('toggleDropdown') toggle?: ElementRef;
	@ViewChild('optionsMenu') optionMenu?: ElementRef;
	/**
	 * If the language list drop down is open.
	 */
	public swapLanguageOpen = false;
	/**
	 * The menu element
	 */
	public myMenu: GapMenu;
	/**
	 * The current language
	 */
	public currentLanguage: string;
	/**
	 * Languages options supported by merchant
	 */
	public supportedLanguages$: Observable<string[]>;
	/**
	 * Mutation Observer for menu dropdown
	 * When dropdown menu is open,
	 * we need to listen for changes in gap-open attribute
	 * cannot set keydown listener inside menu/shadowDom when open
	 * @private
	 */
	private _changes: MutationObserver;

	/**
	 * Our constructor
	 * @param store The global store
	 */
	constructor(private store: Store<AppState>) {
		this.store.select(selectLanguage).subscribe((lang) => {
			this.currentLanguage = lang;
		});

		this.supportedLanguages$ = this.store.select(selectAppConfig(['supported_languages'])).pipe(
			map(({ supported_languages }) => {
				if (validString(supported_languages)) {
					return supported_languages.split(',').map((lang: string) => {
						return {
							name: lang,
							locale: lang.replace(/-|\s/gi, '_'),
						};
					});
				}
				return [];
			})
		);
	}

	/**
	 * After the view is initialized, we need to set up the event listeners for the dropdown
	 */
	public ngAfterViewInit(): void {
		if (document.getElementById('my-menu')) {
			this.myMenu = document.getElementById('my-menu') as GapMenu;
			this.swapLanguageOpen = !!this.myMenu?.gapOpen;
			this.toggle?.nativeElement?.addEventListener('keydown', this._keyDown.bind(this));
			this.optionMenu?.nativeElement?.addEventListener('GapMenu:selected', this._menuSelected.bind(this));

			/**
			 * Listening for menu attributes changes to gap-open
			 * Use for esc & tab keydown events in menu dropdown
			 */
			this._changes = new MutationObserver((mutations: MutationRecord[]) => {
				mutations.forEach((mutation: MutationRecord) => {
					if (mutation.attributeName === 'gap-open') {
						this.swapLanguageOpen = this.myMenu.gapOpen;
					}
				});
			});

			// Setting observable to listen for attributes changes on menu
			this._changes.observe(this.myMenu, {
				attributes: true,
				childList: false,
				characterData: false,
			});
		}
	}

	/**
	 * After the view is destroyed, we need to remove the event listeners
	 */
	public ngOnDestroy(): void {
		this.toggle?.nativeElement?.removeEventListener('keydown', this._keyDown.bind(this));
		this.optionMenu?.nativeElement?.removeEventListener('GapMenu:selected', this._menuSelected.bind(this));
		if (this.myMenu) {
			this._changes.disconnect();
		}
	}

	/**
	 * Change the current language in store to the value given
	 * @param language language value to change
	 */
	public swapLanguage(language: string): void {
		this.store.dispatch(setLanguage({ payload: language }));
	}

	/**
	 * Toggle swap language menu drop down
	 */
	public clickToggleDropdown(): void {
		this.swapLanguageOpen = !this.swapLanguageOpen;
	}

	/**
	 * Toggle swap language menu drop down.
	 * Needed for accessibility keydown event
	 * @private
	 */
	private _toggleDropdown(): void {
		this.swapLanguageOpen = !this.swapLanguageOpen;
	}

	/**
	 * Handle keydown event for accessibility
	 * @param e KeyboardEvent
	 * @private
	 */
	private _keyDown(e: KeyboardEvent): void {
		if (e.key === 'Enter' || e.keyCode === 13 || e.key === 'Space' || e.keyCode === 32) {
			e.preventDefault();
			this._toggleDropdown();
		}
	}

	/**
	 * Handle the menu selected event
	 * @param event The custom event
	 * @private
	 */
	private _menuSelected(event: CustomEvent): void {
		this.swapLanguage(event?.detail?.item?.value);
	}
}
